// src/api/endpoints.ts

const auth = '/web/auth/v1';
const booking = '/web/bookings/v1';
const community = '/web/community/v1';
const dashboard = '/web/dashboard/v1';
const notification = '/web/notifications/v1';
const image = '/api/images/v1';
// 채팅은 앱과 같은 /api 마운트를 쓴다 — /web 미러가 없다.
const chat = '/api/chat/v1';
const service = '/web/services/v1';
const user = '/web/user/v1';
const validation = '/web/validation/v1';
// device는 서버가 URL 마운트(/web vs /api)로 판별 — 웹은 반드시 /web 로 보낼 것.
const interactions = '/web/interactions/v1';

export const ENDPOINTS = {
  auth: {
    //웹 계정 탈퇴
    withdrawalAccount: `${auth}/account`,
    //웹 로그아웃
    signOut: `${auth}/logout`,
    //웹 소셜 리다이렉트
    redirectSocialLogin: (provider: string) => `${auth}/${provider}/redirect`,
    //웹 로그인
    signIn: `${auth}/login`,
    //웹 회원가입
    signUp: `${auth}/register`,
    //웹 토큰 재발급
    refreshToken: `${auth}/tokens`,
  },

  chat: {
    // 회원 (Bearer)
    conversations: () => `${chat}/conversations`,
    conversation: (uuid: String) => `${chat}/conversations/${uuid}`,
    read: (uuid: String) => `${chat}/conversations/${uuid}/read`,
    messages: (uuid: String) => `${chat}/conversations/${uuid}/messages`,
    report: (uuid: String, messageId: number) => `${chat}/conversations/${uuid}/messages/${messageId}/reports`,
    mute: (uuid: String) => `${chat}/conversations/${uuid}/mute`,
    translation: (uuid: String, messageId: number) => `${chat}/conversations/${uuid}/messages/${messageId}/translation`,
    guestTranslation: (bookingUuid: String, messageId: number) => `${chat}/guest/bookings/${bookingUuid}/messages/${messageId}/translation`,
    block: (uuid: String) => `${chat}/conversations/${uuid}/block`,
    // 비회원 — 토큰이 없어 예약 uuid 가 증표라 주소가 예약으로 잡힌다.
    guestConversation: (bookingUuid: String) => `${chat}/guest/bookings/${bookingUuid}/conversation`,
    guestMessages: (bookingUuid: String) => `${chat}/guest/bookings/${bookingUuid}/messages`,
    guestRead: (bookingUuid: String) => `${chat}/guest/bookings/${bookingUuid}/read`,
  },

  booking: {
    //비회원 예약 번호 검색
    // Guest lookup now takes the booking number AND the email on the booking, as a
    // POST body — the number alone is not a credential, and the email must not end
    // up in a URL or an access log.
    searchNonMemberBooking: () => `${booking}/search`,
    //예약 정보 수정 요청 상태 변경 (유저 취소 요청)
    updateBookingStatus: (uuid: String) => `${booking}/${uuid}/update-requests`,
    //예약 리뷰 생성 및 업데이트  
    updateBookingReview: (uuid: String) => `${booking}/${uuid}/reviews`,
    //예약 취소
    cancelBooking: (uuid: String) => `${booking}/${uuid}/cancellation`,
    //예약 결제
    paymentBooking: (uuid: String) => `${booking}/${uuid}/payment`,
    // 서버 측 PG 결제: prepare(서버가 금액을 정해 PG 주문 생성) → PG 승인 → confirm(서버가 캡처·검증·기록)
    preparePayment: (uuid: String, gateway: string) => `${booking}/${uuid}/payments/${gateway}/prepare`,
    confirmPayment: (uuid: String, gateway: string) => `${booking}/${uuid}/payments/${gateway}/confirm`,
    //예약 디테일
    bookingDetil: (uuid: String) => `${booking}/${uuid}`,
    //예약 리스트
    //예약 생성
    bookingList: `${booking}`,
    //예약 정보 수정 요청 리스트 조회
    //예약 정보 수정 요청 생성
    updateBooking: (uuid: String) => `${booking}/${uuid}/update-requests`,
    //예약 히스토리 조회
    bookingHistory: (uuid: String) => `${booking}/${uuid}/histories`,
  },

  community: {
    //커뮤니티 화면 폼
    form: community,
    //커뮤니티 리스트
    list: `${community}/posts`,
    //커뮤니티 디테일
    detail: (uuid: string) => `${community}/posts/${uuid}`,
    //커뮤니티 생성 / 업데이트 (uuid 없으면 생성, 있으면 업데이트)
    save: (uuid?: string) => uuid ? `${community}/posts/${uuid}` : `${community}/posts`,
    //커뮤니티 삭제
    remove: (uuid: string) => `${community}/posts/${uuid}`,
    //댓글 생성 / 업데이트
    saveComment: (postUuid: string, commentUuid?: string) =>
      commentUuid
        ? `${community}/posts/${postUuid}/comments/${commentUuid}`
        : `${community}/posts/${postUuid}/comments`,
    //댓글 삭제
    removeComment: (uuid: string) => `${community}/comments/${uuid}`,
    //좋아요 토글 (likeable_type: posts | comments)
    like: (type: string, uuid: string) =>
      `${community}/${type.toLowerCase()}/${uuid}/likes`,
    //신고 (reportable_type: posts | comments)
    report: (type: string, uuid: string) =>
      `${community}/${type.toLowerCase()}/${uuid}/reports`,
    //커뮤니티 유저 프로필
    userProfile: (uuid: string) => `${community}/users/${uuid}`,
    //@멘션 유저 검색
    mentionSearch: `${community}/mention-search`,
    //상품(서비스) 태그 검색
    productSearch: `${community}/product-search`,
    //게시글/댓글 자동 번역 (?lang=)
    postTranslation: (uuid: string) => `${community}/posts/${uuid}/translation`,
    commentTranslation: (uuid: string) => `${community}/comments/${uuid}/translation`,
    //번역 대상 언어 선호 (GET/PATCH, 회원 전용)
    translationPreferences: '/web/v1/me/translation-preferences',
  },

  dashboard: {
    //대시보드
    dashboard: dashboard,
    //북마크 타입별 UUID 리스트
    syncBookmarks: `${dashboard}/sync-bookmarks`,
    //읽지 않은 신규 알림 체크
    newNotifications: `${dashboard}/new-notifications`,
  },

  notification: {
    list: `${notification}`,
    item: (id: number) => `${notification}/${id}`,
    markAllRead: `${notification}`,
    deleteAll: `${notification}`,
  },

  service: {
    category: (categoryId: string) => `${service}/categories/${categoryId}`,
    list: `${service}/search`,
    detail: (uuid: string) => `${service}/${uuid}`,
    // 서비스 상세 engagement(스크롤 깊이/체류) 수집 — Phase C
    engagement: (uuid: string) => `${service}/${uuid}/engagement`,
    //서비스 상품 검색 초기 화면 (추천 키워드 + 검색 기록)
    searchHome: `${service}/home`,
    //검색어 자동완성(typeahead) — 추천/인기/다국어 유의어 키워드 제안
    searchSuggest: `${service}/search/suggest`,
    //서비스 상품 북마크 리스트
    bookmarks: `${service}/bookmarks`,
    //북마크 저장/해제 toggle
    toggleBookmark: (uuid: string) => `${service}/${uuid}/bookmark`,
    //리뷰 리스트 (type: services | providers | users)
    reviewList: (type: string, uuid: string) => `${service}/${type}/${uuid}/reviews`,
    //리뷰 디테일
    reviewDetail: (uuid: string) => `${service}/reviews/${uuid}`,
    //리뷰 온디맨드 번역 (커뮤니티와 같은 패턴)
    reviewTranslation: (uuid: string) => `${service}/reviews/${uuid}/translation`,
    //예약 생성 전 서비스 정보 조회
    serviceBookingInfo: (uuid: string) => `${service}/${uuid}/booking`,
    //그룹(합류형) 출발 목록 — 날짜별 예약 인원/잔여석 (게스트 허용)
    serviceDepartures: (uuid: string) => `${service}/${uuid}/departures`,
    //상품 문의 접수 (게스트 허용) — 고액 맞춤형은 즉시결제보다 상담이 전환이다
    serviceInquiries: (uuid: string) => `${service}/${uuid}/inquiries`,
    //검색 히스토리 삭제
    searchHistories: `${service}/search-histories`,
    //프로바이더 프로필 조회
    providerProfile: (uuid: string) => `${service}/providers/${uuid}`,
    //비자 국가 리스트 (서버 관리형)
    visa: `${service}/visa`,
  },

  user: {
    //프로필 업데이트
    updateUserInfo: `${user}/profile`,
    //유저 쿠폰 발급
    couponsDownload: (uuid: string) => `${user}/coupons/${uuid}`,
    //유저 쿠폰 디테일
    couponsDetail: (uuid: string) => `${user}/coupons/${uuid}`,
    //유저 쿠폰 리스트
    couponsList: `${user}/coupons`,
  },

  validation: {
    validationEmail: (email: string) => `${validation}/email/${encodeURIComponent(email)}`,
    validationUsername: (name: string) => `${validation}/username/${encodeURIComponent(name)}`,
  },

  image: {
    // presigned URL 직접 업로드.
    presign: `${image}/presign`,
  },

  interactions: {
    // 카테고리/메뉴 클릭 집계 (fire-and-forget, 항상 200). /web 마운트로 web 태깅.
    click: `${interactions}/click`,
  },
} as const;